import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Square, BookOpen, X, Video, VideoOff } from 'lucide-react';
import { useCamera } from '../../hooks/useCamera';
import { useSignRecognition } from '../../hooks/useSignRecognition';
import { useSessionStore } from '../../store/sessionStore';
import { CurrentSignCard } from './CurrentSignCard';
import { SignHistoryChips } from './SignHistoryChips';
import { SignGuide } from './SignGuide';

export const CameraPanel: React.FC = () => {
  const { videoRef, isActive, error, startCamera, stopCamera, captureFrame } = useCamera();
  const {
    isRecording,
    setIsRecording,
    handsDetected,
    recognitionPhase,
  } = useSessionStore();
  const [showGuide, setShowGuide] = useState(false);

  useSignRecognition(captureFrame);

  useEffect(() => {
    if (isActive && !isRecording) setIsRecording(true);
    if (!isActive && isRecording) setIsRecording(false);
  }, [isActive, isRecording, setIsRecording]);

  const handleToggleRecording = async () => {
    if (isRecording) {
      setIsRecording(false);
      stopCamera();
    } else {
      await startCamera();
    }
  };

  return (
    <div className="flex flex-col gap-3 h-full">

      {/* ── Video Card (always visible, expands to fill) ── */}
      <div
        className="relative rounded-2xl overflow-hidden flex-1 min-h-0"
        style={{
          background: '#060d1f',
          border: '1px solid rgba(14,165,233,0.18)',
          boxShadow: '0 0 0 1px rgba(14,165,233,0.06), 0 24px 48px rgba(0,0,0,0.5)',
        }}
      >
        {/* Corner brackets */}
        <div className="absolute top-3 left-3 w-5 h-5 z-20 pointer-events-none"
          style={{ borderTop: '2px solid rgba(14,165,233,0.7)', borderLeft: '2px solid rgba(14,165,233,0.7)', borderRadius: '3px 0 0 0' }} />
        <div className="absolute top-3 right-3 w-5 h-5 z-20 pointer-events-none"
          style={{ borderTop: '2px solid rgba(14,165,233,0.7)', borderRight: '2px solid rgba(14,165,233,0.7)', borderRadius: '0 3px 0 0' }} />
        <div className="absolute bottom-3 left-3 w-5 h-5 z-20 pointer-events-none"
          style={{ borderBottom: '2px solid rgba(14,165,233,0.7)', borderLeft: '2px solid rgba(14,165,233,0.7)', borderRadius: '0 0 0 3px' }} />
        <div className="absolute bottom-3 right-3 w-5 h-5 z-20 pointer-events-none"
          style={{ borderBottom: '2px solid rgba(14,165,233,0.7)', borderRight: '2px solid rgba(14,165,233,0.7)', borderRadius: '0 0 3px 0' }} />

        {/* Scanline overlay */}
        <div className="absolute inset-0 z-10 pointer-events-none scan-overlay" />

        {/* Live badge */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30">
          <motion.div
            animate={{ opacity: isActive ? [1, 0.6, 1] : 0.5 }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold tracking-wider"
            style={{
              background: isActive ? 'rgba(20,184,166,0.15)' : 'rgba(100,116,139,0.15)',
              border: `1px solid ${isActive ? 'rgba(20,184,166,0.4)' : 'rgba(100,116,139,0.3)'}`,
              backdropFilter: 'blur(8px)',
            }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: isActive ? '#2dd4bf' : '#64748b' }}
            />
            <span style={{ color: isActive ? '#2dd4bf' : '#64748b' }}>
              {isActive ? 'AO VIVO' : 'CÂMERA OFF'}
            </span>
          </motion.div>
        </div>

        {isActive && (
          <div
            className="absolute bottom-4 left-4 z-30 flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold"
            style={{
              background: handsDetected ? 'rgba(20,184,166,0.18)' : 'rgba(245,158,11,0.16)',
              border: `1px solid ${handsDetected ? 'rgba(45,212,191,0.4)' : 'rgba(251,191,36,0.35)'}`,
              backdropFilter: 'blur(10px)',
              color: handsDetected ? '#5eead4' : '#fbbf24',
            }}
            data-testid="hand-detection-status"
          >
            <span
              className="w-2 h-2 rounded-full"
              style={{ background: handsDetected ? '#2dd4bf' : '#f59e0b' }}
            />
            {handsDetected
              ? recognitionPhase === 'interpreting' || recognitionPhase === 'partial'
                ? 'Interpretando sinal...'
                : 'Mãos detectadas'
              : 'Mostre as mãos por completo'}
          </div>
        )}

        {/* Guide toggle button */}
        <button
          onClick={() => setShowGuide(v => !v)}
          className="absolute top-4 right-4 z-30 flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-semibold transition-all"
          style={{
            background: showGuide ? 'rgba(14,165,233,0.2)' : 'rgba(13,23,48,0.7)',
            border: `1px solid ${showGuide ? 'rgba(14,165,233,0.5)' : 'rgba(255,255,255,0.1)'}`,
            backdropFilter: 'blur(8px)',
            color: showGuide ? '#38bdf8' : '#94a3b8',
          }}
        >
          {showGuide ? <X className="w-3.5 h-3.5" /> : <BookOpen className="w-3.5 h-3.5" />}
          {showGuide ? 'Fechar' : 'Guia'}
        </button>

        {/* Video element — always rendered */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
          style={{ display: 'block', minHeight: '200px' }}
        />

        {/* Camera off state */}
        {!isActive && !error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center z-20"
            style={{ background: 'rgba(6,13,31,0.92)' }}>
            <VideoOff className="w-12 h-12 mb-3" style={{ color: 'rgba(148,163,184,0.4)' }} />
            <p className="text-slate-500 text-sm font-medium">Câmera desligada</p>
            <p className="text-slate-600 text-xs mt-1">Clique em Iniciar para começar</p>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center z-20"
            style={{ background: 'rgba(6,13,31,0.95)' }}>
            <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
              style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)' }}>
              <VideoOff className="w-6 h-6 text-red-400" />
            </div>
            <p className="text-red-400 text-sm font-medium">{error}</p>
          </div>
        )}

        {/* SignGuide overlay — slides up FROM INSIDE the video panel */}
        {showGuide && (
          <motion.div
            key="guide"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            className="absolute inset-0 z-40 overflow-y-auto"
            style={{
              background: 'rgba(5,12,30,0.96)',
              backdropFilter: 'blur(12px)',
            }}
          >
            <SignGuide onClose={() => setShowGuide(false)} />
          </motion.div>
        )}
      </div>

      {/* ── Current Sign Card ── */}
      <CurrentSignCard />

      {/* ── Sign History ── */}
      <SignHistoryChips />

      {/* ── Start/Stop Button ── */}
      <button
        onClick={handleToggleRecording}
        className="flex items-center justify-center gap-3 w-full py-3.5 rounded-2xl font-semibold text-sm tracking-wide transition-all active:scale-[0.98]"
        style={isRecording ? {
          background: 'linear-gradient(135deg, #ef4444, #dc2626)',
          boxShadow: '0 4px 20px rgba(239,68,68,0.3)',
          color: 'white',
          border: 'none',
        } : {
          background: 'linear-gradient(135deg, #0ea5e9, #14b8a6)',
          boxShadow: '0 4px 20px rgba(14,165,233,0.35)',
          color: 'white',
          border: 'none',
        }}
      >
        {isRecording ? (
          <>
            <Square className="w-4 h-4 fill-current" />
            PARAR RECONHECIMENTO
          </>
        ) : (
          <>
            <Video className="w-4 h-4" />
            INICIAR RECONHECIMENTO
          </>
        )}
      </button>
    </div>
  );
};
